'use client';

import { useEffect } from 'react';
import { debug } from '@/lib/utils/debug';
import {
    cleanupChunkRecoveryUrl,
    isChunkLoadError,
    isStaticChunkUrl,
    recoverFromChunkError,
} from '@/lib/utils/chunkError';

/**
 * Global safety net for stale-chunk deploys.
 *
 * Catches chunk-load failures that never reach the React error boundary:
 *  - resource load errors on injected <script>/<link> tags for `_next/static`
 *    (these only surface on the capture phase of the window `error` event), and
 *  - rejected dynamic `import()` promises (`unhandledrejection`).
 *
 * On a match it triggers a single guarded reload to pull a fresh build. The
 * React `ErrorBoundary` covers chunk errors thrown during render; this covers
 * the rest.
 */
export function ChunkErrorRecovery(): null {
    useEffect(() => {
        // A previous recovery reload may have added a cache-bust marker — remove
        // it now that the app booted successfully.
        cleanupChunkRecoveryUrl();

        const handleResourceOrError = (event: ErrorEvent): void => {
            const target = event.target as (HTMLScriptElement & HTMLLinkElement) | null;
            if (target && (target.tagName === 'SCRIPT' || target.tagName === 'LINK')) {
                const src = target.src || target.href || '';
                if (isStaticChunkUrl(src)) {
                    debug.warn('ChunkErrorRecovery: static asset failed to load', src);
                    recoverFromChunkError();
                }
                return;
            }
            if (isChunkLoadError(event.error)) {
                debug.warn('ChunkErrorRecovery: chunk error event', event.message);
                recoverFromChunkError();
            }
        };

        const handleRejection = (event: PromiseRejectionEvent): void => {
            if (isChunkLoadError(event.reason)) {
                debug.warn('ChunkErrorRecovery: chunk import rejection');
                recoverFromChunkError();
            }
        };

        // Capture phase is required to observe resource (script/link) load errors.
        window.addEventListener('error', handleResourceOrError, true);
        window.addEventListener('unhandledrejection', handleRejection);

        return () => {
            window.removeEventListener('error', handleResourceOrError, true);
            window.removeEventListener('unhandledrejection', handleRejection);
        };
    }, []);

    return null;
}
